import { motion } from 'framer-motion';
import { Coins, Settings2, Target, Lightbulb, TrendingUp } from 'lucide-react';

/**
 * CloudHubImpactDeck, a native React re-render of the original
 * "CLOUDHUB IMPACT" slide from the enterprise 2024 case-study deck.
 *
 * The original was a JPEG dropped into the case study. This component keeps
 * the deck's visual identity (deep-blue background, warm-amber headings,
 * cyan iconography) while gaining editorial polish, motion, and theme-aware
 * text, so the impact story reads as a first-class section rather than a
 * pasted-in artefact.
 */

const impacts = [
    {
        Icon: Coins,
        title: '~$6M saved since 2018',
        bullets: [
            'Use resources only when needed',
            'Flexible & Scheduled ON/OFF',
            'Monitor utilisation & recommend',
        ],
    },
    {
        Icon: Settings2,
        title: 'Simple, Automated & Secure Provisioning',
        bullets: [
            'Avg. time to provision < 1 hour',
            'Self-service & automated provisioning',
            'Pre-defined and sanitised cloud landing zones',
            '99% of resources tagged',
        ],
    },
    {
        Icon: Target,
        title: 'Visibility & Right-sizing',
        bullets: [
            'Monitors use and recommends downsize',
            'Self-service optimisation',
            '~15% of compute resources optimised monthly',
        ],
    },
    {
        Icon: Lightbulb,
        title: 'Cloud Resource Visibility',
        bullets: [
            'Assets in cloud (cross region & accounts)',
            'Current & historical cost for cloud assets',
            'FinOps: 100% of monthly bill attributed to Programs & Cost Centers',
        ],
    },
];

const easeOut = [0.2, 0.8, 0.2, 1];

export const CloudHubImpactDeck = () => (
    <motion.section
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-6%' }}
        transition={{ duration: 0.7, ease: easeOut }}
        data-testid="cloudhub-impact-deck"
        className="relative mb-8 md:mb-12 rounded-[22px] overflow-hidden border border-[rgba(255,255,255,0.06)]"
        style={{
            background:
                'radial-gradient(120% 100% at 0% 0%, #1e60c9 0%, #12417d 40%, #0b2957 75%, #071b3d 100%)',
        }}
    >
        {/* Ambient blueprint texture, very faint cross-hatch, echoes the original slide */}
        <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-[0.05]"
            style={{
                backgroundImage:
                    'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
                backgroundSize: '48px 48px',
                mixBlendMode: 'overlay',
            }}
        />
        {/* Soft glow disc */}
        <div
            aria-hidden
            className="pointer-events-none absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full"
            style={{
                background:
                    'radial-gradient(circle, rgba(103, 232, 249, 0.18) 0%, transparent 60%)',
            }}
        />

        <div className="relative p-8 md:p-12">
            {/* Header, dashed rule + amber title, echoes the source deck */}
            <div className="flex items-center gap-3 mb-6 md:mb-8">
                <TrendingUp size={16} className="text-cyan-300" />
                <span className="font-mono-brand text-[10.5px] uppercase tracking-[0.28em] text-cyan-100/70">
                    Enterprise SaaS · 2024
                </span>
                <span className="flex-1 h-px bg-white/10 border-t border-dashed border-white/15" />
            </div>

            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-6 md:mb-10">
                <div>
                    <h3
                        className="font-display text-4xl md:text-6xl leading-[0.98] tracking-tight"
                        style={{ color: '#f5c95a' }}
                    >
                        CloudHub Impact
                    </h3>
                    <p className="mt-3 font-sans-brand text-lg md:text-2xl text-white/90">
                        Increased conversion sales by{' '}
                        <span className="font-semibold text-white">30%</span>.
                    </p>
                </div>
                {/* Big lime accent pill mirroring the case-study language */}
                <div className="shrink-0 inline-flex items-center gap-3 rounded-full bg-white/8 border border-white/12 backdrop-blur px-4 py-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-300 shadow-[0_0_12px_2px_rgba(110,231,183,0.65)]" />
                    <span className="font-mono-brand text-[11px] uppercase tracking-[0.24em] text-white/80">
                        Shipped · in production
                    </span>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                {impacts.map(({ Icon, title, bullets }, i) => (
                    <motion.article
                        key={i}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-4%' }}
                        transition={{ delay: 0.15 + i * 0.08, duration: 0.55, ease: easeOut }}
                        data-testid={`cloudhub-impact-card-${i}`}
                        className="group relative rounded-2xl bg-white/[0.04] border border-white/[0.09] p-6 md:p-7 hover:bg-white/[0.06] hover:border-white/[0.16] transition-colors"
                    >
                        <div className="flex items-start gap-4">
                            <div className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-xl bg-cyan-300/12 text-cyan-200 group-hover:text-cyan-100 transition-colors">
                                <Icon size={22} strokeWidth={1.8} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <h4
                                    className="font-display leading-tight text-xl md:text-2xl"
                                    style={{ color: '#f5c95a' }}
                                >
                                    {title}
                                </h4>
                                <ul className="mt-3 flex flex-col gap-2">
                                    {bullets.map((b, j) => (
                                        <li
                                            key={j}
                                            className="flex items-start gap-2.5 text-[13.5px] md:text-[14px] leading-[1.55] text-white/85"
                                        >
                                            <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-cyan-300/80 shrink-0" />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </motion.article>
                ))}
            </div>

            <p className="mt-8 md:mt-10 font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-white/45">
                Original impact deck · rebuilt natively for this case study
            </p>
        </div>
    </motion.section>
);

export default CloudHubImpactDeck;
